/**
 * Optional fallback for city-level geography and VPN/proxy signals.
 *
 * Preferred path is the edge (meta tags / Cloudflare) — it costs nothing, adds no
 * latency, and the visitor's IP never leaves your infrastructure.
 *
 * Trade-offs of using a third-party provider:
 *   • the visitor's IP is sent to a third party, which belongs in your privacy policy
 *   • free tiers are rate-limited well below a few hundred thousand page views
 *
 * The lookup runs off the critical path and is cached per browser session.
 */
export const geoConfig: {
  providerUrl: string
  countryField: string
  cityField: string
  timezoneField: string
  vpnField: string | null
  orgField: string
  asnField: string
  ipField: string
} = {
  /** e.g. 'https://ipapi.co/json/' or 'https://ipinfo.io/json?token=…' */
  providerUrl: 'https://ipapi.co/json/',
  /** Response property holding the ISO country code. */
  countryField: 'country_code',
  /** Response property holding the city name. */
  cityField: 'city',
  /**
   * Response property holding the IANA time zone of the IP. Comparing it with the
   * browser's own time zone is the VPN heuristic — they disagree when traffic is
   * tunnelled through another region.
   */
  timezoneField: 'timezone',
  /**
   * Optional: a boolean property from a paid plan that flags proxies/VPNs
   * directly (ipapi.co Threat plans, ipinfo.io `privacy.vpn`). Set to null when
   * unavailable and the heuristic above is used alone.
   */
  vpnField: null,
  /**
   * Response property holding the ASN organisation that owns the IP — this is what
   * reveals *which* network (NordVPN, M247, AWS, Zscaler …) traffic arrives on.
   * ipapi.co returns 'org'; ipinfo.io returns 'org' as "AS#### Name".
   */
  orgField: 'org',
  /** Response property holding the AS number itself. */
  asnField: 'asn',
  /**
   * Response property holding the caller's IP.
   *
   * The address is truncated to its network block (/24 for IPv4, /48 for IPv6)
   * IN THE BROWSER and only the block is ever sent onward. The full address is
   * never stored, logged, or displayed: a block identifies a VPN exit node or a
   * corporate egress without identifying a person.
   */
  ipField: 'ip'
}
